export interface PipeDimension {
  npsCode: string;
  npsLabel: string;
  od: number;
  schedule: number | string | null;
  designation: string | null;
  thickness: number;
  standard: string;
}

export interface PipeMaterial {
  family: string;
  specification: string;
  grade: string;
  standard: string;
}

export interface PipingClass {
  code: string;
  description: string;
  materialFamily: string;
  specification: string;
  grade: string;
  corrosionAllowance: number;
  designCode: string;
  pressureClass: string;
}

export class PipingEngineeringCatalogService {
  private dimensions: PipeDimension[] = [];
  private materials: PipeMaterial[] = [];
  private classes: PipingClass[] = [];
  private loaded = false;

  async loadCatalogs() {
    if (this.loaded) return;
    try {
      const [dimRes, matRes, clsRes] = await Promise.all([
        fetch('/catalogs/pipe-dimensions.json'),
        fetch('/catalogs/pipe-materials.json'),
        fetch('/catalogs/piping-classes.json')
      ]);
      this.dimensions = await dimRes.json();
      this.materials = await matRes.json();
      this.classes = await clsRes.json();
      this.loaded = true;
    } catch (e) {
      console.error('Error loading piping catalogs:', e);
      // fallback mock se não rodar num servidor web com /catalogs/
      this.dimensions = [];
      this.materials = [];
      this.classes = [];
    }
  }

  getMaterialFamilies(): string[] {
    return Array.from(new Set(this.materials.map(m => m.family)));
  }

  getSpecifications(family: string): string[] {
    return Array.from(new Set(this.materials.filter(m => m.family === family).map(m => m.specification)));
  }

  getGrades(family: string, spec: string): string[] {
    return Array.from(new Set(this.materials.filter(m => m.family === family && m.specification === spec).map(m => m.grade)));
  }

  getDimensionalStandard(family: string): string {
    if (family === 'Aço Inoxidável') return 'ASME B36.19M';
    return 'ASME B36.10M';
  }

  getAvailableNPS(standard: string): { code: string, label: string }[] {
    const dimensions = this.dimensions.filter(d => d.standard === standard);
    const unique = new Map<string, string>();
    dimensions.forEach(d => {
      if (!unique.has(d.npsCode)) {
        unique.set(d.npsCode, d.npsLabel);
      }
    });

    // Parse fraction or integer for sorting
    const parseNpsValue = (nps: string) => {
      if (nps.includes('/')) {
        const parts = nps.split(' ');
        if (parts.length === 2) {
          const part0 = parts[0] as string;
          const part1 = parts[1] as string;
          const fraction = part1.split('/');
          const num = fraction[0] as string;
          const den = fraction[1] as string;
          return parseFloat(part0) + (parseFloat(num) / parseFloat(den));
        } else {
          const part0 = parts[0] as string;
          const fraction = part0.split('/');
          const num = fraction[0] as string;
          const den = fraction[1] as string;
          return parseFloat(num) / parseFloat(den);
        }
      }
      return parseFloat(nps);
    };

    return Array.from(unique.entries())
      .map(([code, label]) => ({ code, label, val: parseNpsValue(code) }))
      .sort((a, b) => a.val - b.val)
      .map(({ code, label }) => ({ code, label }));
  }

  getAvailableSchedules(npsCode: string, standard: string): { value: string, label: string }[] {
    const matching = this.dimensions.filter(d => d.npsCode === npsCode && d.standard === standard);
    const results = new Map<string, string>();
    matching.forEach(d => {
      const schVal = d.schedule !== null ? `Sch ${d.schedule}` : '';
      const desigVal = d.designation ? d.designation : '';
      
      let label = schVal;
      if (schVal && desigVal) label += ` (${desigVal})`;
      if (!schVal && desigVal) label = desigVal;
      
      const valStr = JSON.stringify({ schedule: d.schedule, designation: d.designation });
      if (!results.has(valStr)) {
        results.set(valStr, label);
      }
    });
    return Array.from(results.entries()).map(([value, label]) => ({ value, label }));
  }

  getDimension(npsCode: string, scheduleInfo: string, standard: string): PipeDimension | undefined {
    try {
      const { schedule, designation } = JSON.parse(scheduleInfo);
      return this.dimensions.find(d => 
        d.npsCode === npsCode && 
        d.schedule === schedule && 
        d.designation === designation && 
        d.standard === standard
      );
    } catch {
      return undefined;
    }
  }

  getPipingClasses(): PipingClass[] {
    return this.classes;
  }

  getPipingClass(code: string): PipingClass | undefined {
    return this.classes.find(c => c.code === code);
  }

  calculateInnerDiameter(od: number, thickness: number): number {
    return Number((od - (2 * thickness)).toFixed(2));
  }
}

export const pipingEngineeringService = new PipingEngineeringCatalogService();
